import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';

// Stripe Checkout Session レスポンスの型定義
interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

// 銀行振込サブスクリプション作成 レスポンスの型定義
interface BankTransferSubscriptionResponse {
  success: boolean;
  subscriptionId: string;
  customerId: string;
  hostedInvoiceUrl?: string | null;
  invoicePdf?: string | null;
  trialEnd?: number; // トライアル終了日（Unix timestamp秒）
}

// 銀行振込先（Funding Instructions）レスポンス
export interface BankTransferInstructionsResponse {
  bankName: string | null;
  branchName: string | null;
  accountNumber: string | null;
  accountType: string | null;
  accountHolderName: string | null;
  referenceCode: string | null;
}

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
  futsu: '普通預金',
  toza: '当座預金',
};

export const formatBankAccountType = (type?: string | null): string | null => {
  if (!type || !type.trim()) {
    return null;
  }

  const normalized = type.trim().toLowerCase();
  return ACCOUNT_TYPE_LABELS[normalized] || type;
};

// PaymentMethod情報の型定義
export interface PaymentMethodInfo {
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
}

export class StripeService {
  // Stripe Price IDs (テスト環境)
  static readonly planPriceIds: { [key: string]: string } = {
    'プラン50': 'price_1SGqmJRwyiwnT83EqDXdrxx3',
    'プラン100': 'price_1SGqoMRwyiwnT83EHJIX7cmA',
    'プラン200': 'price_1SGqojRwyiwnT83Efphf54cd',
    'プラン300': 'price_1SGqp2RwyiwnT83E4cN3vETh',
    'プラン400': 'prod_TFdIkorcbMCXk6',
    'プラン500': 'prod_TFdKeJXYoeRd4m',
  };

  // 環境変数キーのマッピング
  private static readonly priceEnvKeyMap: Record<string, string> = {
    'プラン50': 'VITE_PRICE_PLAN50',
    'プラン100': 'VITE_PRICE_PLAN100',
    'プラン200': 'VITE_PRICE_PLAN200',
    'プラン300': 'VITE_PRICE_PLAN300',
    'プラン400': 'VITE_PRICE_PLAN400',
    'プラン500': 'VITE_PRICE_PLAN500',
  };

  private static normalizePlanName(raw: string): string {
    const trimmed = raw.trim();
    
    // 完全一致をチェック
    if (this.planPriceIds[trimmed]) {
      return trimmed;
    }
    
    // 全角・半角空白の除去
    const normalized = trimmed.replace(/[\u3000\s]+/g, '');
    
    // 正規化後の完全一致
    if (this.planPriceIds[normalized]) {
      return normalized;
    }
    
    return trimmed; // 最後の手段
  }

  public static resolvePriceId(planName: string): { key: string; priceId?: string } {
    const key = this.normalizePlanName(planName);
    const envKey = this.priceEnvKeyMap[key];
    const env = import.meta.env as Record<string, string | undefined>;
    const priceId = envKey ? env[envKey] : undefined;
    return { key, priceId };
  }

  /**
   * Checkout Sessionを作成してStripe決済ページへリダイレクト（トークンベース）
   */
  static async startCheckoutWithToken(token: string, planName: string): Promise<void> {
    try {
      const { key: normalizedPlanName, priceId } = this.resolvePriceId(planName);
      
      if (!priceId) {
        throw new Error('無効なプランが選択されました');
      }

      // Checkoutセッション作成前に、Price ID解決が期待通りかを確認する
      console.log('[Stripe Debug] startCheckoutWithToken resolved', {
        planName,
        normalizedPlanName,
        priceId,
      });

      // Firebase Functions Callable を呼び出し
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSessionWithToken');
      
      const result = await createCheckoutSession({
        token: token,
        priceId: priceId,
        planName: normalizedPlanName,
      });

      const sessionUrl = (result.data as CheckoutSessionResponse).url;
      console.log('[Stripe Debug] checkout session url received', { sessionUrl });

      // Stripe Checkout ページへリダイレクト
      window.location.href = sessionUrl;
      
    } catch (error: any) {
      console.error('[Stripe] Checkout error:', error);
      
      let errorMessage = '決済処理の開始に失敗しました。しばらく時間をおいてから再度お試しください';
      
      // エラーの種類に応じてメッセージを変更
      if (error.message?.includes('network-request-failed')) {
        errorMessage = 'ネットワークエラーが発生しました。インターネット接続を確認してください';
      } else if (error.message?.includes('permission-denied')) {
        errorMessage = '決済処理の権限がありません。管理者にお問い合わせください';
      } else if (error.message?.includes('invalid-argument')) {
        errorMessage = '決済情報に問題があります。入力内容をご確認ください';
      } else if (error.message?.includes('quota-exceeded')) {
        errorMessage = '決済処理の利用上限に達しました。しばらく時間をおいてから再度お試しください';
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * 銀行振込用サブスクリプションを作成（トークンベース）
   */
  static async createSubscriptionWithBankTransfer(token: string, planName: string): Promise<BankTransferSubscriptionResponse> {
    try {
      const { key: normalizedPlanName, priceId } = this.resolvePriceId(planName);
      
      if (!priceId) {
        throw new Error('無効なプランが選択されました');
      }

      // Firebase Functions Callable を呼び出し
      const createSubscription = httpsCallable(functions, 'createSubscriptionWithBankTransfer');
      
      const result = await createSubscription({
        token: token,
        priceId: priceId,
        planName: normalizedPlanName,
      });

      const response = result.data as BankTransferSubscriptionResponse;

      return response;
      
    } catch (error: any) {
      console.error('[Stripe] Bank transfer subscription error:', error);
      
      let errorMessage = '銀行振込の登録に失敗しました。しばらく時間をおいてから再度お試しください';
      
      // エラーの種類に応じてメッセージを変更
      if (error.message?.includes('network-request-failed')) {
        errorMessage = 'ネットワークエラーが発生しました。インターネット接続を確認してください';
      } else if (error.message?.includes('permission-denied')) {
        errorMessage = '登録処理の権限がありません。管理者にお問い合わせください';
      } else if (error.message?.includes('invalid-argument')) {
        errorMessage = '登録情報に問題があります。入力内容をご確認ください';
      } else if (error.message?.includes('quota-exceeded')) {
        errorMessage = '登録処理の利用上限に達しました。しばらく時間をおいてから再度お試しください';
      }
      
      throw new Error(errorMessage);
    }
  }

  /**
   * 銀行振込の口座情報（Funding Instructions）を取得
   */
  static async getBankTransferInstructions(customerId: string): Promise<BankTransferInstructionsResponse> {
    const getInstructions = httpsCallable(functions, 'getBankTransferInstructions');
    const result = await getInstructions({ customerId });
    const data = result.data as any;
    return {
      bankName: data.bankName || null,
      branchName: data.branchName || null,
      accountNumber: data.accountNumber || null,
      accountType: data.accountType || null,
      accountHolderName: data.accountHolderName || null,
      referenceCode: data.referenceCode || null,
    };
  }

  /**
   * カード情報（PaymentMethod）を取得
   */
  static async getPaymentMethod(customerId: string): Promise<PaymentMethodInfo | null> {
    try {
      const getPaymentMethod = httpsCallable(functions, 'getPaymentMethod');
      const result = await getPaymentMethod({ customerId });
      const data = result.data as any;
      return data.paymentMethod;
    } catch (error: any) {
      console.error('[Stripe] Get payment method error:', error);
      throw new Error('カード情報の取得に失敗しました');
    }
  }

  /**
   * カード変更用のCheckout Sessionを作成
   */
  static async createCardUpdateSession(customerId: string, companyId: string): Promise<string> {
    try {
      const createSession = httpsCallable(functions, 'createCardUpdateSession');
      const result = await createSession({ customerId, companyId });
      const data = result.data as { url: string };
      return data.url;
    } catch (error: any) {
      console.error('[Stripe] Create card update session error:', error);
      throw new Error('カード変更セッションの作成に失敗しました');
    }
  }
}
